'use client'

import { useState } from 'react'
import { useAuth } from '@/components/providers/supabase-auth-provider'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import { Check, Loader2, Clock, Shield, ArrowRight, Crown, Sparkles } from 'lucide-react'
import {
  TIER_FEATURES,
  TRIAL_CONFIG,
  type BillingInterval,
} from '@/config/subscriptions'
import { useRouter } from 'next/navigation'
import { useSubscription } from '@/lib/hooks/use-subscription'

// Stripe payment links for Professional tier
const PAYMENT_LINKS = {
  monthly: 'https://buy.stripe.com/8wM3dF7aFatX1LG5kl',
  annual: 'https://buy.stripe.com/7sY8wO64LfxEfLAaK1gIo06',
}

export default function PricingPage() {
  const { user } = useAuth()
  const { tier: currentTier, subscription } = useSubscription()
  const router = useRouter()
  const [loading, setLoading] = useState(false)
  const [billingInterval, setBillingInterval] = useState<BillingInterval>('month')

  const isCurrentPlan = user && currentTier === 'professional'
  const professionalFeatures = TIER_FEATURES.professional

  // Start free trial — always route through /signup which handles everything
  const handleStartTrial = () => {
    router.push('/signup')
  }

  const handleSubscribe = () => {
    if (isCurrentPlan) return

    setLoading(true)
    const paymentLink = billingInterval === 'year' ? PAYMENT_LINKS.annual : PAYMENT_LINKS.monthly
    const finalUrl = user ? `${paymentLink}?client_reference_id=${user.id}` : paymentLink
    window.location.href = finalUrl
  }

  const showTrialButton = !user || (user && currentTier === 'free' && subscription?.status !== 'trialing')

  return (
    <div className="container max-w-2xl mx-auto px-4 pt-10 pb-16 dashboard-cards">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold tracking-tight mb-2">Simple, transparent pricing</h1>
        <p className="text-muted-foreground">Everything you need to win government contracts — one plan, no surprises.</p>

        {/* Billing Toggle */}
        <div className="inline-flex items-center gap-3 bg-muted/50 dark:bg-white/5 rounded-full px-5 py-2 border border-border/50 mt-6">
          <span className={`text-sm font-semibold transition-colors ${billingInterval === 'month' ? 'text-foreground' : 'text-muted-foreground'}`}>
            Monthly
          </span>
          <Switch
            checked={billingInterval === 'year'}
            onCheckedChange={(checked) => setBillingInterval(checked ? 'year' : 'month')}
          />
          <span className={`text-sm font-semibold transition-colors ${billingInterval === 'year' ? 'text-foreground' : 'text-muted-foreground'}`}>
            Annual
          </span>
          {billingInterval === 'year' && (
            <Badge className="bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/25 font-semibold text-xs">
              Save $40
            </Badge>
          )}
        </div>
      </div>

      {/* Single Professional Card */}
      <div className="relative">
        <div className="absolute -inset-px rounded-2xl bg-gradient-to-b from-sky-500 via-blue-500 to-indigo-500 opacity-100" />
        <Card className="relative flex flex-col rounded-2xl border-transparent shadow-xl shadow-blue-500/10 dark:shadow-blue-500/5 overflow-hidden">
          <div className="absolute top-0 inset-x-0 flex justify-center">
            <div className="bg-gradient-to-r from-sky-500 to-blue-600 text-white text-xs font-bold uppercase tracking-wider px-4 py-1 rounded-b-lg flex items-center gap-1.5">
              <Crown className="w-3 h-3" /> Professional Plan
            </div>
          </div>

          <CardHeader className="pt-8 px-6 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-xl font-bold flex items-center gap-2">
                  {professionalFeatures.name}
                  {isCurrentPlan && (
                    <Badge className="bg-sky-500/15 text-sky-600 dark:text-sky-400 border-sky-500/25 text-[10px] font-semibold">
                      Current Plan
                    </Badge>
                  )}
                </CardTitle>
                <CardDescription className="mt-1 text-sm">
                  {professionalFeatures.description}
                </CardDescription>
              </div>
            </div>

            <div className="mt-4 pb-4 border-b border-border/50">
              <div className="flex items-baseline gap-1">
                <span className="text-4xl font-extrabold tracking-tight">
                  ${billingInterval === 'year' ? professionalFeatures.annualPrice : professionalFeatures.price}
                </span>
                <span className="text-sm font-medium text-muted-foreground">
                  /{billingInterval === 'year' ? 'yr' : 'mo'}
                </span>
              </div>
              {billingInterval === 'year' && (
                <p className="text-sm text-emerald-600 dark:text-emerald-400 font-medium mt-1">
                  Save $40/year vs monthly billing
                </p>
              )}
              {billingInterval === 'month' && (
                <p className="text-sm text-muted-foreground mt-1">
                  Or $200/year — save $40
                </p>
              )}
            </div>
          </CardHeader>

          <CardContent className="px-6 pb-6 flex flex-col gap-6">
            {/* Features */}
            <ul className="space-y-2.5">
              {professionalFeatures.features.map((feature, index) => (
                <li key={index} className="flex items-start gap-2.5">
                  <div className="rounded-full p-0.5 mt-0.5 shrink-0 bg-sky-500/15 text-sky-600 dark:text-sky-400">
                    <Check className="h-3 w-3" />
                  </div>
                  <span className="text-sm text-foreground/80 leading-relaxed">{feature}</span>
                </li>
              ))}
            </ul>

            {/* CTAs */}
            <div className="space-y-2">
              {showTrialButton && (
                <Button
                  className="w-full border-sky-500/30 text-sky-600 dark:text-sky-400 hover:bg-sky-500/10"
                  variant="outline"
                  onClick={handleStartTrial}
                >
                  <Clock className="mr-2 h-4 w-4" />
                  Start {TRIAL_CONFIG.durationDays}-Day Free Trial
                </Button>
              )}

              <Button
                className="w-full bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white shadow-md shadow-blue-500/20 font-semibold"
                disabled={loading || !!isCurrentPlan}
                onClick={handleSubscribe}
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Please wait...
                  </>
                ) : isCurrentPlan ? (
                  'Current Plan'
                ) : (
                  <>
                    Subscribe Now
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </>
                )}
              </Button>

              {showTrialButton && (
                <p className="text-xs text-muted-foreground text-center pt-1">
                  No credit card required for trial
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Bottom CTA */}
      <div className="mt-8 text-center">
        <div className="inline-flex items-center gap-2 text-xs text-muted-foreground">
          <Shield className="h-3.5 w-3.5 text-sky-500" />
          <span>SSL encryption, SOC 2 compliance, and 99.9% uptime.</span>
        </div>
        <p className="mt-1.5 text-xs text-muted-foreground">
          Have questions? <Button variant="link" className="p-0 text-xs text-sky-600 dark:text-sky-400 font-semibold" onClick={() => router.push('/contact')}>Contact our team</Button>
        </p>
      </div>
    </div>
  )
}
